import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { LandingPageSettings } from '../../services/landingPageApi';

interface BasicInfoTabProps {
  data: LandingPageSettings;
  onSave: (updates: Partial<LandingPageSettings>) => void;
  isSaving: boolean;
}

export const BasicInfoTab = ({ data, onSave, isSaving }: BasicInfoTabProps) => {
  const [formData, setFormData] = useState<LandingPageSettings>({
    headline: '',
    subheadline: '',
    bio: '',
    contactEmail: '',
    phone: '',
    website: '',
    linkedIn: '',
    instagram: '',
    showServices: true,
  });

  useEffect(() => {
    setFormData({
      headline: data.headline || '',
      subheadline: data.subheadline || '',
      bio: data.bio || '',
      contactEmail: data.contactEmail || '',
      phone: data.phone || '',
      website: data.website || '',
      linkedIn: data.linkedIn || '',
      instagram: data.instagram || '',
      showServices: data.showServices ?? true,
    });
  }, [data]);

  const handleChange = (field: keyof LandingPageSettings) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSwitchChange = (field: keyof LandingPageSettings) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Page Content
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Headline"
            value={formData.headline}
            onChange={handleChange('headline')}
            fullWidth
            placeholder="Your Partner in Productivity"
            helperText="The main headline visitors will see"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Subheadline"
            value={formData.subheadline}
            onChange={handleChange('subheadline')}
            fullWidth
            multiline
            rows={2}
            placeholder="I help busy professionals reclaim their time and focus on what matters most."
            helperText="A brief tagline or value proposition"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="About Me / Bio"
            value={formData.bio}
            onChange={handleChange('bio')}
            fullWidth
            multiline
            rows={5}
            placeholder="Tell visitors about yourself, your experience, and how you help clients..."
            helperText={`${formData.bio?.length || 0}/1000 characters`}
            inputProps={{ maxLength: 1000 }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 3 }}>
        Contact Information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Contact Email"
            type="text"
            value={formData.contactEmail}
            onChange={handleChange('contactEmail')}
            fullWidth
            placeholder="hello@yourdomain.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
            fullWidth
            placeholder="+1 (555) 123-4567"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Website"
            value={formData.website}
            onChange={handleChange('website')}
            fullWidth
            placeholder="https://yourwebsite.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WebsiteIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="LinkedIn"
            value={formData.linkedIn}
            onChange={handleChange('linkedIn')}
            fullWidth
            placeholder="your-profile"
            helperText="Just your profile name, not the full URL"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkedInIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Instagram"
            value={formData.instagram}
            onChange={handleChange('instagram')}
            fullWidth
            placeholder="@yourhandle"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InstagramIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 3 }}>
        Display Options
      </Typography>

      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={formData.showServices}
              onChange={handleSwitchChange('showServices')}
            />
          }
          label="Show my services on the landing page"
        />
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          sx={{ px: 4 }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};
